// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Carousel.tsx
 * @input Uses React, StyleX, useScrollOverflow, useLayer, Button, Icon, theme tokens
 * @output Exports Carousel component
 * @position Horizontal scroll container with fade-edge overflow indication,
 *   optional prev/next buttons on the top layer, scroll-snap, and a 1px
 *   visual bleed allowance for child selection indicators.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Carousel/index.ts (exports)
 * - /apps/storybook/stories/Carousel.stories.tsx
 * - /packages/cli/templates/blocks/components/Carousel/ (showcase blocks)
 */
import { useRef, useCallback, useEffect, Children } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars, colorVars, shadowVars, radiusVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { Button } from "../Button/index.js";
import { Icon } from "../Icon/index.js";
import { useLayer } from "../Layer/index.js";
import { useScrollOverflow } from "../hooks/useScrollOverflow.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    k7Eaqz: "xeuugli",
    ks0D6T: "x193iq5w",
    kVQacm: "x7giv3",
    kOmJVX: "x9y5y3v",
    $$css: true
  },
  scroller: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kXHlph: "xw2csxc",
    kORKVm: "x10wlt62",
    kGO01o: "x1j85h84",
    k1K539: "x1jyxor1",
    kvh3hx: "x7p5m3t",
    khEr5k: "xkcv6ua xg83hb1",
    k5wCbM: "x1rohswg",
    kX1K2I: "xw4ya9i",
    k1ekBW: "x157bb6t",
    kIyJzY: "x80gvsz x12w9bfk",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  fadeStart: {
    kX1K2I: "x1ujl4f5",
    $$css: true
  },
  fadeEnd: {
    kX1K2I: "xtdd0ob",
    $$css: true
  },
  fadeBoth: {
    kX1K2I: "x1b5cd1y",
    $$css: true
  },
  snap: {
    kc424l: "xhfbhpw",
    $$css: true
  },
  buttonOverlay: {
    k1xSpc: "x78zum5",
    kjj79g: "x1qughib",
    kGNEyG: "x6s0dn4",
    kfzvcC: "x47corl",
    $$css: true
  },
  buttonRadiusOverride: {
    "--_button-radius": "x1rjw8qi",
    $$css: true
  }
};
const gapStyles = {
  "0": {
    kOIVth: "xsn7fz1",
    $$css: true
  },
  "1": {
    kOIVth: "xzye2dw",
    $$css: true
  },
  "2": {
    kOIVth: "x1txdalj",
    $$css: true
  },
  "3": {
    kOIVth: "xjcht0a",
    $$css: true
  },
  "4": {
    kOIVth: "x18g69wz",
    $$css: true
  },
  "0.5": {
    kOIVth: "x1lsbc85",
    $$css: true
  },
  "1.5": {
    kOIVth: "x1s4dlld",
    $$css: true
  }
};
const paddingStyles = {
  "0": {
    kg3NbH: "x17a0aqu",
    katHQp: "x9lgdif",
    $$css: true
  },
  "1": {
    kg3NbH: "x7a5moj",
    katHQp: "x1u2972t",
    $$css: true
  },
  "2": {
    kg3NbH: "xf314gf",
    katHQp: "x1mfb6cv",
    $$css: true
  },
  "3": {
    kg3NbH: "xrrkdod",
    katHQp: "xpnzzb2",
    $$css: true
  },
  "4": {
    kg3NbH: "x1pzlopt",
    katHQp: "x3m3szd",
    $$css: true
  },
  "5": {
    kg3NbH: "xzz68wx",
    katHQp: "x1vx7sv0",
    $$css: true
  },
  "6": {
    kg3NbH: "xm7rs69",
    katHQp: "x1mvw9yj",
    $$css: true
  },
  "8": {
    kg3NbH: "x6xnjw4",
    katHQp: "x1xn2tv3",
    $$css: true
  },
  "10": {
    kg3NbH: "xixulwi",
    katHQp: "xw959t8",
    $$css: true
  },
  "0.5": {
    kg3NbH: "xkjnx3w",
    katHQp: "xdf8p4l",
    $$css: true
  },
  "1.5": {
    kg3NbH: "x4ezzuc",
    katHQp: "x1w8vatw",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * Horizontal scroll container with fade-edge overflow indication and
 * optional navigation buttons.
 *
 * Wraps any content in a scrollable row. When content overflows, gradient
 * fades appear at the edges to signal more items exist. When content overflows, prev/next buttons appear at the edges,
 * rendered on the top layer via Layer so they escape any parent overflow
 * clipping.
 *
 * @example
 * ```
 * <Carousel gap={1}>
 *   <Thumbnail src="/a.jpg" alt="A" />
 *   <Thumbnail src="/b.jpg" alt="B" />
 *   <Thumbnail src="/c.jpg" alt="C" />
 * </Carousel>
 * ```
 */
export function Carousel({
  ref,
  children,
  gap = 1,
  hasButtons = true,
  hasEdgeFade = true,
  hasSnap = false,
  padding,
  'aria-label': ariaLabelFromProps,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ...htmlProps
}) {
  const t = useTranslator();
  const ariaLabel = ariaLabelFromProps ?? t('@astryx.carousel.label');
  const scrollElRef = useRef(null);
  const {
    scrollRef,
    overflowStart,
    overflowEnd
  } = useScrollOverflow();
  const layer = useLayer({
    mode: 'context',
    lightDismiss: false
  });
  useEffect(() => {
    if (hasButtons) {
      layer.show();
    } else {
      layer.hide();
    }
  }, [hasButtons, layer]);
  const composedRef = useCallback(el => {
    scrollElRef.current = el;
    scrollRef(el);
  }, [scrollRef]);
  const scrollBy = useCallback(direction => {
    const el = scrollElRef.current;
    if (!el) {
      return;
    }
    const firstChild = el.firstElementChild;
    const itemWidth = firstChild ? firstChild.offsetWidth : 0;
    const amount = el.clientWidth - itemWidth * 0.5;
    // Respect the user's reduced-motion preference — mirrors the CSS
    // scroll-behavior override so button-driven scrolling doesn't animate
    // for users who opted out of motion.
    const prefersReducedMotion = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({
      left: direction * Math.max(amount, itemWidth),
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }, []);
  const fadeStyle = hasEdgeFade ? overflowStart && overflowEnd ? styles.fadeBoth : overflowStart ? styles.fadeStart : overflowEnd ? styles.fadeEnd : null : null;

  // Self-authored position styles (positioning: 'custom' below): a cover
  // centered on the anchor, sized to it — direction-neutral by construction.
  const coverStyle = {
    positionArea: 'center',
    width: 'anchor-size(width)',
    height: 'anchor-size(height)'
  };
  return /*#__PURE__*/_jsxs("div", {
    ref: mergeRefs(ref, layer.ref),
    "data-testid": testId,
    ...htmlProps,
    ...mergeProps(themeProps('carousel'), stylex.props(styles.root, xstyle), className, style),
    role: "region",
    "aria-label": ariaLabel,
    "aria-roledescription": "carousel",
    children: [/*#__PURE__*/_jsx("div", {
      ref: composedRef,
      tabIndex: 0,
      ...stylex.props(styles.scroller, gapStyles[gap], padding != null && paddingStyles[padding], hasSnap && styles.snap, fadeStyle),
      children: Children.map(children, child => /*#__PURE__*/_jsx("div", {
        ...{
          className: "x1bhewko x78zum5 x2lah0s"
        },
        children: child
      }))
    }), hasButtons && layer.render(/*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        ...{
          0: {
            className: "x78zum5 x6s0dn4 xl56j7k x1prclbq xjspbzw x14hfi27 x67bb7w x1hc1fzr x19991ni xuedmi6 xlr8y92 xuuh30"
          },
          1: {
            className: "x78zum5 x6s0dn4 xl56j7k x1prclbq xjspbzw x14hfi27 x19991ni xuedmi6 xlr8y92 xuuh30 xg01cxk x47corl"
          }
        }[!!!overflowStart << 0],
        children: /*#__PURE__*/_jsx(Button, {
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "chevronLeft",
            size: "xsm"
          }),
          label: t('@astryx.carousel.scrollLeft'),
          variant: "ghost",
          size: "sm",
          isIconOnly: true
          // Disabled when there's nothing to scroll toward. Keeps the
          // button mounted (stable layout/focus) but removes it from the
          // tab order and a11y tree while it's visually hidden, so
          // keyboard users don't land on an invisible control.
          ,
          isDisabled: !overflowStart,
          onClick: () => scrollBy(-1),
          xstyle: styles.buttonRadiusOverride
        })
      }), /*#__PURE__*/_jsx("div", {
        ...{
          0: {
            className: "x78zum5 x6s0dn4 xl56j7k x1prclbq xjspbzw x14hfi27 x67bb7w x1hc1fzr x19991ni xuedmi6 xlr8y92 xitnhlw"
          },
          1: {
            className: "x78zum5 x6s0dn4 xl56j7k x1prclbq xjspbzw x14hfi27 x19991ni xuedmi6 xlr8y92 xitnhlw xg01cxk x47corl"
          }
        }[!!!overflowEnd << 0],
        children: /*#__PURE__*/_jsx(Button, {
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "chevronRight",
            size: "xsm"
          }),
          label: t('@astryx.carousel.scrollRight'),
          variant: "ghost",
          size: "sm",
          isIconOnly: true
          // See "Scroll left" — disabled while visually hidden so the
          // button stays mounted but out of the tab order / a11y tree.
          ,
          isDisabled: !overflowEnd,
          onClick: () => scrollBy(1),
          xstyle: styles.buttonRadiusOverride
        })
      })]
    }), {
      positioning: 'custom',
      style: coverStyle,
      xstyle: styles.buttonOverlay
    })]
  });
}
Carousel.displayName = 'Carousel';